import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ArrowUp } from 'lucide-react'; 
import Header from '../components/Header';
import { mockData } from "@/mockData";
import './ArticleDetail.css';

// ==========================================
// ARTICLE 1: Riot Games
// ==========================================
const riotGamesArticle = {
  id: 1, 
  title: "League of Legends Broke Down not because of a hacker but Carelessness",
  category: "Product Security Case Study",
  date: "Jan 4, 2026",
  readTime: "6 min read",
  mediumUrl: "https://medium.com/@BhowalWarden/league-of-legends-broke-down-not-because-of-a-hacker-but-carelessness-a-product-security-wake-up-1cb69e20cad9",
  excerpt: "What happened on Jan 4th 2026 wasn’t just a login issue—it was a textbook Product Security incident caused by a single expired certificate. Here is the deep dive into why trust chains collapse and why automation is non-negotiable.",
  
  content: (
    <>
      <p className="article-intro">
        Do you know Riot Games? Have you played Countless Sessions of Valorant — I have. 
        Then what happened on <strong>4th January 2026</strong> wasn’t just a frustrating login issue but a real-world 
        Product Security incident that exposed how a single expired certificate can disrupt a global, cloud-native platform.
      </p>
      <p>
        For gamers, it meant downtime, but for security engineers, it was a textbook example of operational security risk.
      </p>

      <h2>🕹️ Why League of Legends Matters</h2>
      <p>
        League of Legends is not just entertainment — it’s a massively distributed live-service product running on 
        complex cloud infrastructure with:
      </p>
      <ul>
        <li>Millions of daily authenticated users</li>
        <li>Encrypted client–server communication</li>
        <li>Continuous deployment pipelines</li>
        <li>Trust-based endpoint validation</li>
      </ul>
      <p>
        When something breaks at this scale, it <strong>becomes a case study</strong> in Product & Cloud Security.
      </p>

      <h2>🔍 What Actually Happened?</h2>
      <p>
        The outage was caused by an expired SSL/TLS security certificate — originally issued in 2016 with a 10-year validity.
      </p>
      <p>Once the certificate expires:</p>
      <ul>
        <li>Operating systems stopped trusting the LoL client</li>
        <li>Secure connections failed</li>
        <li>Authentication and patching also broke</li>
      </ul>
      <p>
        In essence, this wasn’t a vulnerability exploit, but rather a <strong>certificate lifecycle management failure</strong>.
        And from a Product Security standpoint, that’s critical ☠️
      </p>

      <h2>🔐 Why Certificate Expiration Is Critical</h2>
      <p>Because Certificates are foundational to four main things:</p>
      <ul>
        <li>👉 Secure communication (TLS)</li>
        <li>👉 Identity verification</li>
        <li>👉 Zero Trust architectures</li>
        <li>👉 Protection against man-in-the-middle attacks</li>
      </ul>
      <p>
        When certificates expire in production, the <strong>Trust chains collapse</strong>. Clients fail closed as they should, 
        and the user experience degrades instantly. This incident shows how non-code security dependencies can be just as 
        dangerous as insecure code.
      </p>

      <h2>🔄 Déjà Vu: A Repeated Oversight</h2>
      <p>
        What makes this more concerning is that a nearly identical outage happened in January 2016, for the same reason. 
        From a security engineering lens, this raises red flags around:
      </p>
      <div className="highlight-box">
        👉 Asset inventory & ownership | Automated certificate rotation | Monitoring & alerting | Secure SDLC operational maturity
      </div>
      <p>In modern cloud environments, manual certificate tracking is a risk.</p>

      <h2>⏰ The Dangerous Community “Fix”</h2>
      <p>Players quickly discovered a workaround: <strong>Backdating system time to bypass certificate validation.</strong></p>
      <p>As a Product Security Engineer, this is alarming:</p>
      <ul>
        <li>Bypasses trust validation</li>
        <li>Weakens endpoint security posture</li>
        <li>Introduces MITM (Man in the Middle Attack) and spoofing risks</li>
        <li>Breaks OS-level security assumptions</li>
      </ul>
      <div className="alert-box">
        ⚠️ This highlights a recurring problem: When secure systems fail without clear guidance, users choose unsafe workarounds.
      </div>

      <h2>☁️ The Intersection of Disciplines</h2>
      <p>
        This outage perfectly demonstrates the intersection of: <strong>Product Security</strong> (trust, identity, certificates), 
        <strong>Cloud Security</strong> (distributed systems, availability), and <strong>Reliability Engineering</strong> (resilience & failover).
      </p>
      <p>
        Even a multi-billion-dollar company like Riot Games is vulnerable to small operational oversights. 
        Security isn’t just about preventing attacks — it’s about maintaining trust continuously.
      </p>

      <h2>🔐🎮 Gaming + Security: The Bigger Lesson</h2>
      <p>
        Modern games are Cloud-hosted products with Zero-trust systems and Security-dependent services. 
        Every login, match, and update relies on valid certificates, secure identity verification, and operational security discipline. 
        Gamers may never see it — until it breaks.
      </p>

      <h2>🎮 Final Thoughts</h2>
      <p>The League of Legends Certificate Outage of 2026 wasn’t just downtime — it was a reminder that:</p>
      
      <h3>🔑 Key Product Security Takeaways:</h3>
      <ul>
        <li>Certificate lifecycle management is critical</li>
        <li>👉 Automation is better manual processes</li>
        <li>👉 Operational security failures impact real users</li>
        <li>👉 Trust is fragile and must be actively maintained</li>
      </ul>
      
      <blockquote>
        For security professionals, this is a reminder: Security doesn’t fail loudly — it fails suddenly.
      </blockquote>

      <hr />
      
      <div className="references-section">
        <h3>🔗 References & Sources</h3>
        <ul>
          <li>
            <a 
              href="https://www.pcgamer.com/games/moba/league-of-legends-players-worldwide-couldnt-login-for-hours-because-riot-forgot-to-renew-the-clients-ssl-certificate-just-like-it-did-10-years-ago/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00F0FF', textDecoration: 'none' }}
            >
              PC Gamer — Coverage on the League of Legends certificate outage
            </a>
          </li>
          <li>
            <a 
              href="https://www.eurogamer.net/oops-league-of-legends-was-briefly-unplayable-as-riot-games-forgot-to-renew-the-digital-licence"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00F0FF', textDecoration: 'none' }}
            >
              Eurogamer — Riot Games digital license expiration report
            </a>
          </li>
          <li>
            <a 
              href="https://www.gamesradar.com/games/league-of-legends/league-of-legends-was-completely-down-yesterday-unless-you-reset-your-system-clock-because-riot-forgot-to-update-a-security-certificate/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00F0FF', textDecoration: 'none' }}
            >
              GamesRadar — Community workaround analysis
            </a>
          </li>
          <li>
            <a 
              href="https://support.riotgames.com/hc/en-us"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00F0FF', textDecoration: 'none' }}
            >
              Riot Games Support — Official service updates
            </a>
          </li>
        </ul>
      </div>
    </>
  )
};

// ==========================================
// ARTICLE 2: What is Product Security?
// ==========================================
const productSecurityArticle = {
  id: 2,
  title: "What Is Product Security? (Explained Like You're Talking to a Friend)",
  category: "Product Security 101",
  date: "Jan 24, 2026",
  readTime: "5 min read",
  mediumUrl: "https://medium.com/@BhowalWarden/what-is-product-security-explained-like-youre-talking-to-a-friend-185293dd1836", 
  excerpt: "Product security is about building safety into a product from the outset, rather than trying to fix problems after something goes wrong. Here's a breakdown of threat modeling, secure coding, and why the 'Tea Analogy' perfectly explains AppSec.",

  content: (
    <>
      <p className="article-intro">
        If you’ve ever used an app, a website, or even a smart device, you’ve already interacted with <strong>product security</strong> — whether you realized it or not.
      </p>
      <p>
        In simple terms, product security is about building safety into a product from the outset, rather than trying to fix problems after something goes wrong. It’s the mindset of asking:
      </p>
      <div className="highlight-box">
        👉 “How could this product be misused, broken, or attacked — and how do we prevent that?”
      </div>
      <p>
        In today’s digital world, where almost everything is connected to the internet, this matters more than ever. Data breaches, hacked apps, leaked passwords — most of these don’t happen because people are careless <em>after</em> launch, but because security wasn’t considered <em>early enough</em>.
      </p>
      <p>And that’s exactly where product security steps in.</p>

      <h2>🔍 Breaking Down Product Security (Without the Buzzwords)</h2>
      <p>
        When people hear terms like <strong>threat modeling</strong>, <strong>secure coding</strong>, or <strong>OWASP Top 10</strong>, it can feel overwhelming. Let’s slow it down and explain these ideas like we would to a friend.
      </p>

      <h3>1. Threat Modeling</h3>
      <p>This is simply <strong>thinking like an attacker.</strong> You look at your product and ask:</p>
      <ul>
        <li>What parts are exposed?</li>
        <li>Where could someone sneak in?</li>
        <li>What would be most damaging if compromised?</li>
      </ul>
      <p>It’s not about hacking — it’s about <strong>anticipating problems before attackers do.</strong></p>

      <h3>2. Secure Coding</h3>
      <p>
        Secure coding means <strong>writing code that doesn’t make life easy for attackers.</strong> For example:
      </p>
      <ul>
        <li>Not trusting user input blindly</li>
        <li>Handling passwords properly</li>
        <li>Avoiding shortcuts that can later become vulnerabilities</li>
      </ul>
      <p>Think of it like locking doors while building a house, not after someone breaks in.</p>

      <h3>3. Vulnerability Assessments</h3>
      <p>
        These are <strong>regular checkups</strong> for your product. You scan or test the application to find weak spots — just like a health check before issues become serious.
      </p>

      <h3>4. Incident Response</h3>
      <p>
        Even with great security, things can still go wrong. Incident response is your <strong>plan for damage control:</strong>
      </p>
      <ul>
        <li>What happens if something breaks?</li>
        <li>Who gets alerted?</li>
        <li>How fast can you fix it?</li>
      </ul>
      <p>Preparation here can save a company’s reputation.</p>

      <h2>☕ The Tea Analogy (Because It Just Works)</h2>
      <p>Let’s revisit the analogy you shared — because it’s actually spot on.</p>
      
      <blockquote>
        <p><strong>Imagine you’re making tea:</strong></p>
        <ul>
          <li>You boil water</li>
          <li>You collect the ingredients</li>
          <li>You make sure the cup, spoon, and kettle are clean</li>
          <li>Only then do you start mixing everything</li>
        </ul>
        <p>
          Product security is that hygiene check <strong>before</strong> cooking. You don’t wait until someone gets sick to check if the water was dirty. You make sure everything is safe <strong>before</strong> serving.
        </p>
      </blockquote>
      
      <p>
        That’s what product security does for software — it ensures safety <strong>before the product ever goes live on the internet.</strong>
      </p>

      <h2>🤔 So… Why Does Product Security Feel So Big?</h2>
      <p>Because it touches <strong>everything</strong>:</p>
      <ul>
        <li>Design</li>
        <li>Code</li>
        <li>Testing</li>
        <li>Deployment</li>
        <li>Updates</li>
      </ul>
      <p>Yes, it includes things like Secure code reviews, CI/CD pipeline security, and SAST & DAST tools. But at its core, product security is not about tools. It’s about <strong>intentional, thoughtful building.</strong></p>

      <h2>🚀 My Learning Journey</h2>
      <p>
        I’m currently learning product security step by step — through courses, hands-on practice, and real-world examples. What surprised me most is how <strong>early decisions matter.</strong>
      </p>
      <p>A small design choice made at the beginning can:</p>
      <ul>
        <li>Prevent hundreds of alerts later</li>
        <li>Save time for security teams</li>
        <li>Protect real users from real harm</li>
      </ul>
      <p>
        And honestly? That mindset shift — from reacting to incidents to <strong>preventing them</strong> — is what hooked me.
      </p>

      <h2>👋 Let Me Ask You This</h2>
      <ul>
        <li>Have you ever used an app and thought, “This feels unsafe”?</li>
        <li>How do companies protect your data behind the scenes?</li>
        <li>If you were building your own app today, would security be an afterthought — or part of the plan?</li>
      </ul>

      <h2>🔒 Final Thoughts: Safety First, Always</h2>
      <p>Product security is not about paranoia. It’s about <strong>responsibility.</strong></p>
      <p>
        It’s about caring enough to build things the right way — secure by design, not secure by accident. I’m still learning, still exploring, and still asking questions. If this topic sparks your curiosity even a little, I’d love for you to join me on this journey.
      </p>
      
      <div className="highlight-box" style={{borderLeftColor: '#00F0FF', marginTop: '40px'}}>
        Because in a world that runs on software, <strong>security isn’t optional — it’s foundational. 🔐</strong>
      </div>
    </>
  )
};

// ==========================================
// ARTICLE 3: Porter Data Breach
// ==========================================
const porterBreachArticle = {
  id: 3,
  title: "A Wake-Up Call: Navigating the Aftermath of the Porter Data Breach",
  category: "Incident Response & Personal Security",
  date: "Jan 24, 2026",
  readTime: "7 min read",
  mediumUrl: "https://medium.com/@BhowalWarden/a-wake-up-call-navigating-the-aftermath-of-the-porter-data-breach-b50be78ab5c9", 
  excerpt: "Received a breach notification? Don't panic. Here is a security analyst's breakdown of what happens behind the scenes at companies like Porter, and a step-by-step guide on how to secure your digital identity immediately.",

  content: (
    <>
      <p className="article-intro">
        Today at 2:57 PM, I received an email from <strong>Porter</strong> about a data breach. As someone with a background in cybersecurity, I know that companies generally take immediate preventive actions when breaches occur, including containment, securing systems, and implementing measures to prevent further damage.
      </p>
      <p>
        However, as a frequent user of the Porter app — and potentially a stakeholder of SmartShift Logistics Solutions Private Limited — I can’t help but reflect on the broader implications of this incident.
      </p>

      <h2>🛡️ Inside the War Room: What Companies Do</h2>
      <p>
        If I were part of the company as a Security Analyst, I would be privy to the various teams actively working on the breach. These include:
      </p>
      <div className="highlight-box">
        <ul style={{marginBottom: 0}}>
          <li><strong>Data Loss Prevention (DLP) Team:</strong> Focused on identifying and mitigating the data exfiltrated during the breach.</li>
          <li><strong>Penetration Testing Team:</strong> Ensuring there are no residual vulnerabilities in the system.</li>
          <li><strong>Threat Hunting Team:</strong> Actively searching for potential attackers or malware still lurking in the network.</li>
          <li><strong>Incident Response Team:</strong> Coordinating efforts to resolve the breach efficiently and documenting learnings.</li>
        </ul>
      </div>
      <p>
        However, in this case, I am not part of the company. I am just a user. This realization raises a crucial question:
      </p>
      <p style={{fontSize: '20px', fontWeight: '700', color: '#fff', borderLeft: '4px solid #00F0FF', paddingLeft: '16px', margin: '32px 0'}}>
        What steps can I, as a user, take to protect myself from potential risks arising from this breach?
      </p>

      <h2>Step 1: Assessing the Scope</h2>
      <p>To address the problem effectively, it’s vital to first understand what data might have been compromised. In the case of Porter, the following information may have been at risk:</p>
      <ul>
        <li><strong>Email Address:</strong> Likely linked to the Porter account for communication.</li>
        <li><strong>Phone Number:</strong> Shared for account verification and updates.</li>
        <li><strong>Personal Information:</strong> Including name and potentially family details.</li>
        <li><strong>Residential Addresses:</strong> Saved addresses for pick-up and delivery.</li>
      </ul>
      <p><em>Fortunately, no financial information such as credit or debit card details was stored, as I handle payments externally via UPI.</em></p>

      <h2>Step 2: Immediate Actions Taken</h2>
      <p>After identifying the potential risks, I implemented several immediate measures to secure my data:</p>
      
      <h3>1. Verify Data Exposure</h3>
      <p>I checked all email addresses linked to my Porter account on <strong>Have I Been Pwned</strong>, a trusted website that identifies whether your data has been part of any known breaches.</p>

      <h3>2. Enable Multi-Factor Authentication (MFA)</h3>
      <p>For all my email accounts, I enabled MFA to add an extra layer of security. I linked my Gmail accounts to my phone for seamless access control.</p>

      <h3>3. Secure Family Accounts</h3>
      <p>I installed the <strong>Microsoft Authenticator App</strong> on my father’s phone and synced it with his compromised email account to enhance its security.</p>

      <h3>4. Log Out of Untrusted Devices</h3>
      <p>I reviewed all devices logged into my email accounts and signed out of any unknown or suspicious devices. Only trusted devices like my personal laptop and mobile phone remained logged in.</p>

      <h3>5. Scan for Malware</h3>
      <p>I installed <strong>MKavach2</strong> and <strong>E-Scan Bot Removal</strong> on my mobile phone to check for potential malware. Additionally, I ensured that my laptop was equipped with a robust antivirus solution.</p>

      <h2>Step 3: Advanced Protective Measures</h2>
      <p>While the initial steps secure your data, advanced measures provide an extra layer of protection:</p>
      <ul>
        <li><strong>Update Critical Account Passcodes:</strong> Changed passwords for Aadhar, PAN, and banking accounts.</li>
        <li><strong>Avoid Storing Passwords on Devices:</strong> Reviewed and deleted any saved passwords stored in browsers.</li>
        <li><strong>Monitor Financial Activity:</strong> Regularly review bank account statements for suspicious activity.</li>
        <li><strong>Watch Out for Phishing:</strong> Cybercriminals may exploit the situation by sending phishing emails pretending to be from Porter.</li>
        <li><strong>Back-Up Important Data:</strong> Regularly back up critical files to secure cloud storage or external drives.</li>
      </ul>

      <h2>Step 4: Cultivate Long-Term Habits</h2>
      <p>Cybersecurity is not a one-time effort. Here are tips to stay secure:</p>
      <div className="alert-box" style={{borderLeftColor: '#00F0FF'}}>
        <ul>
          <li><strong>Use Strong Passwords:</strong> Complex and unique for every account.</li>
          <li><strong>Update Software Regularly:</strong> Patch security vulnerabilities.</li>
          <li><strong>Limit Sharing:</strong> Be cautious about sharing sensitive data.</li>
          <li><strong>Identity Theft Protection:</strong> Consider services like credit monitoring.</li>
        </ul>
      </div>

      <h2>🔒 Final Thoughts: Taking Control</h2>
      <p>
        Data breaches like the one experienced by Porter remind us how interconnected our lives are with digital platforms. While companies are responsible for safeguarding user data, it’s equally important for individuals to take proactive measures.
      </p>
      <p>
        The aftermath of a breach can feel overwhelming, but by understanding the risks and taking swift, informed action, you can minimize potential damage.
      </p>
      <blockquote>
        Remember: Prevention is always better than cure. Stay informed, stay secure, and take control of your digital life.
      </blockquote>
    </>
  )
};

// ==========================================
// COMPONENT LOGIC
// ==========================================
const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  let article;
  const idNum = parseInt(id);

  // LOGIC TO SELECT ARTICLE BASED ON ID
  if (idNum === 1) {
    article = riotGamesArticle;
  } else if (idNum === 2) {
    article = productSecurityArticle;
  } else if (idNum === 3) {
    article = porterBreachArticle;
  } else {
    // Fallback to mockData for any other IDs
    article = mockData.articles.find(a => a.id === idNum);
  }

  useEffect(() => {
    if (!article) {
      navigate('/');
    }
  }, [article, navigate]);

  // Scroll To Top Function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!article) return null;

  return (
    <>
      {/* 2. ADDED HEADER HERE */}
      <Header />
      
      <div className="article-detail-container">
        <button className="back-button" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
          <span>BACK TO PORTFOLIO</span>
        </button>

        <div className="article-content">
          <div className="article-header">
            <span className="article-category">{article.category}</span>
            <h1 className="article-title">{article.title}</h1>
            <div className="article-meta">
              <span>{article.date}</span>
              <span>•</span>
              <span>{article.readTime}</span>
            </div>
          </div>

          <div className="article-redirect">
            <p className="redirect-text">
              This article is published on Medium. Click below to read on the original platform.
            </p>
            <a 
              href={article.mediumUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="medium-link"
            >
              <span>READ ON MEDIUM</span>
              <ExternalLink size={20} />
            </a>
          </div>

          <div className="article-excerpt">
            <h3>Summary</h3>
            <p>{article.excerpt}</p>
          </div>

          {article.content && (
            <div className="medium-body-content article-body-render">
               <hr className="content-divider" style={{margin: '40px 0', borderColor: '#333'}} />
               {article.content}
            </div>
          )}

          {/* BACK TO TOP BUTTON */}
          <button className="scroll-top-button" onClick={scrollToTop}>
              <ArrowUp size={20} />
              <span>BACK TO TOP</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default ArticleDetail;